import React, { useState, useMemo } from 'react';
import { HiChevronDown, HiCollection, HiEye, HiEyeOff } from 'react-icons/hi';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ParameterTooltip } from '../utils/distributionParameters';
import SpecifyDayPanel from './SpecifyDayPanel';

const DistributionsTab = ({ data }) => {
    const [selectedMethod, setSelectedMethod] = useState('EMPÍRICAS (TABELA)');

    // Extract distribution data from backend
    const distributionData = useMemo(() => {
        if (!data || !data.Distribuicoes) {
            return { distAjustadas: null, distEmpiricas: null, distRndForest: null };
        }

        return {
            distAjustadas: data.Distribuicoes.distAjustadas || null,
            distEmpiricas: data.Distribuicoes.distEmpiricas || null,
            distRndForest: data.Distribuicoes.distRndForest || null
        };
    }, [data]);

    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="p-6 text-center text-gray-500">
                <p>Nenhum dado disponível para distribuições</p>
            </div>
        );
    }

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        if (typeof value !== 'number') return value;

        // Usar notação científica para valores muito pequenos (mas não zero)
        if (Math.abs(value) < 0.01 && value !== 0) {
            return value.toExponential(2);
        }

        return value.toFixed(2);
    };

    // Gráfico de distribuição ajustada - Mostra PDF e CDF com duplo eixo Y
    const FittedDistributionChart = ({ transitionKey, distData, empiricalValues }) => {
        const [showPDF, setShowPDF] = useState(true);
        const [showCDF, setShowCDF] = useState(true);
        const [pontoMarcado, setPontoMarcado] = useState(null);

        const chartData = useMemo(() => {
            if (!distData || !distData.distMelhor) {
                return [];
            }

            const { pdfMelhor, cdfMelhor } = distData;

            if (pdfMelhor && Array.isArray(pdfMelhor) && pdfMelhor.length > 0) {
                const numPoints = pdfMelhor.length;

                // Determinar range baseado nos dados empíricos ou fallback
                let minVal = 0;
                let maxVal = 500;

                if (empiricalValues && Array.isArray(empiricalValues) && empiricalValues.length > 0) {
                    minVal = 0;
                    maxVal = Math.max(...empiricalValues);
                }

                return pdfMelhor.map((densidade, index) => {
                    const x = minVal + (index / (numPoints - 1)) * (maxVal - minVal);
                    return {
                        tempo: x,
                        densidade: densidade,
                        cdf: cdfMelhor && cdfMelhor[index] ? cdfMelhor[index] : null
                    };
                });
            }

            return [];
        }, [distData, empiricalValues]);

        if (!distData || !distData.distMelhor || chartData.length === 0) {
            return null;
        }

        const { distribuicao, params, ks_p } = distData.distMelhor;

        return (
            <div className="bg-white rounded-lg p-6 mb-6" style={{ outline: 'none', border: 'none' }}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Distribuição Ajustada da Transição {transitionKey}
                </h3>
                <SpecifyDayPanel
                    distribuicao={distribuicao}
                    params={params}
                    showPDF={showPDF}
                    onPontoMarcado={setPontoMarcado}
                />
                <div style={{ position: 'relative', outline: 'none', border: 'none' }} tabIndex="-1" onMouseDown={(e) => e.preventDefault()}>
                    {/* Legenda customizada */}
                    <div style={{
                        position: 'absolute',
                        top: '15px',
                        right: '170px',
                        padding: '10px',
                        fontSize: '12px',
                        lineHeight: '1.6',
                        zIndex: 10,
                        color: '#000',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        borderRadius: '4px'
                    }}>
                        <div><strong>Distribuição ({distribuicao})</strong></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <ParameterTooltip distribution={distribuicao} parameters={params}>
                                {/* <HiInformationCircle
                                    style={{
                                        cursor: 'help',
                                        color: '#3b82f6',
                                        fontSize: '14px',
                                        marginLeft: '4px'
                                    }}
                                /> */}
                                Parâmetros: {params.map(p => formatNumber(p)).join(', ')}

                            </ParameterTooltip>
                        </div>
                        <div>p_valor (KS) = {formatNumber(ks_p)}</div>

                        <div style={{ marginTop: '10px', borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>
                            {/* <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: 'bold', color: '#6b7280' }}>
                                CURVAS (clique para mostrar/ocultar):
                            </div> */}
                            <div
                                style={{
                                    cursor: 'pointer',
                                    opacity: showPDF ? 1 : 0.5,
                                    // color: showPDF ? '#2563eb' : '#9ca3af',
                                    color: showPDF ? 'black' : '#9ca3af',
                                    marginBottom: '2px',
                                    userSelect: 'none'
                                }}
                                onClick={() => setShowPDF(!showPDF)}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    {showPDF ? <HiEye /> : <HiEyeOff />}
                                    <label>Densidade (PDF)</label>
                                </div>

                            </div>
                            <div
                                style={{
                                    cursor: 'pointer',
                                    opacity: showCDF ? 1 : 0.5,
                                    // color: showCDF ? '#dc2626' : '#9ca3af',
                                    color: showCDF ? 'black' : '#9ca3af',
                                    userSelect: 'none'
                                }}
                                onClick={() => setShowCDF(!showCDF)}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    {showCDF ? <HiEye /> : <HiEyeOff />}
                                    Probabilidade Acumulada (CDF)
                                </div>
                            </div>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={500}>
                        <LineChart data={chartData} margin={{ top: 20, right: 100, left: 80, bottom: 50 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="tempo"
                                type="number"
                                domain={['dataMin', 'dataMax']}
                                label={{ value: 'Valores (dias)', position: 'insideBottom', offset: -10 }}
                                tickFormatter={(value) => {
                                    if (Math.abs(value) > 10000 || (Math.abs(value) < 0.01 && value !== 0)) {
                                        return value.toExponential(1);
                                    }
                                    return value.toFixed(0);
                                }}
                            />
                            {showPDF ?
                                <YAxis
                                    yAxisId="pdf"
                                    orientation="left"
                                    label={{ value: 'Densidade (PDF)', angle: -90, position: 'center', dx: -50, fill: '#2563eb' }}
                                    tickFormatter={(value) => {
                                        if (Math.abs(value) < 0.001 && value !== 0) {
                                            return value.toExponential(1);
                                        }
                                        if (Math.abs(value) > 1000) {
                                            return value.toExponential(1);
                                        }
                                        return value.toFixed(3);
                                    }}
                                /> : null}
                            {showCDF ?
                                <YAxis
                                    yAxisId="cdf"
                                    orientation="right"
                                    domain={[0, 1]}
                                    label={{ value: 'Probabilidade Acumulada (CDF)', angle: 90, position: 'center', dx: 50, fill: '#dc2626' }}
                                    tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                                /> : null}

                            <Tooltip
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length > 0) {
                                        const data = payload[0].payload;
                                        const tempo = data.tempo;
                                        const densidade = data.densidade;
                                        const cdf = data.cdf;

                                        return (
                                            <div style={{
                                                backgroundColor: 'white',
                                                border: '1px solid #ccc',
                                                padding: '10px',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                            }}>
                                                <div><strong>Valor (X):</strong> {formatNumber(tempo)} dias</div>
                                                {showPDF && (
                                                    // <div style={{ color: '#2563eb' }}>
                                                    <div>
                                                        <strong>Densidade (PDF):</strong> {formatNumber(densidade)}
                                                    </div>
                                                )}
                                                {showCDF && cdf !== null && (
                                                    // <div style={{ color: '#dc2626' }}>
                                                    <div >
                                                        <strong>Probabilidade Acumulada (CDF):</strong> {(cdf * 100).toFixed(1)}%
                                                    </div>
                                                )}

                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            {showPDF && (
                                <Line
                                    type="monotone"
                                    dataKey="densidade"
                                    yAxisId="pdf"
                                    stroke="#2563eb"
                                    // stroke="black"
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={false}
                                    isAnimationActive={false}
                                    name="PDF"
                                />
                            )}
                            {showCDF && (
                                <Line
                                    type="monotone"
                                    dataKey="cdf"
                                    yAxisId="cdf"
                                    stroke="#dc2626"
                                    // stroke="black"
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={false}
                                    isAnimationActive={false}
                                    name="CDF"
                                />
                            )}
                            {pontoMarcado && pontoMarcado.cdf !== null && (
                                <ReferenceLine
                                    x={pontoMarcado.valor}
                                    yAxisId={showPDF ? 'pdf' : 'cdf'}
                                    stroke="#16a34a"
                                    strokeWidth={2}
                                    strokeDasharray="4 4"
                                    label={(props) => {
                                        const { viewBox } = props;
                                        return (
                                            <g>
                                                <text x={viewBox.x} y={viewBox.y - 5} fill="#16a34a" fontSize={12} textAnchor="middle">
                                                    {`${(pontoMarcado.cdf * 100).toFixed(1)}%`}
                                                </text>
                                                <text x={viewBox.x} y={viewBox.y + viewBox.height + 15} fill="#16a34a" fontSize={12} textAnchor="middle">
                                                    {`${formatNumber(pontoMarcado.valor)}`}
                                                </text>
                                            </g>
                                        );
                                    }}
                                />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    // Gráfico de distribuição empírica (histograma azul)
    const EmpiricalDistributionChart = ({ transitionKey, values, media, p10, p50, p75, p90 }) => {
        const chartData = useMemo(() => {
            if (!values || !Array.isArray(values) || values.length === 0) {
                return [];
            }

            const minVal = Math.min(...values);
            const maxVal = Math.max(...values);
            const numBins = 20;
            const binWidth = (maxVal - minVal) / numBins;

            const bins = Array(numBins).fill(0).map((_, i) => ({
                start: minVal + i * binWidth,
                end: minVal + (i + 1) * binWidth,
                count: 0
            }));

            values.forEach(val => {
                const binIndex = Math.min(Math.floor((val - minVal) / binWidth), numBins - 1);
                if (binIndex >= 0 && binIndex < numBins) {
                    bins[binIndex].count++;
                }
            });

            return bins.map((bin) => ({
                valores: Math.round((bin.start + bin.end) / 2),
                frequencia: bin.count
            }));
        }, [values]);

        // Encontrar índices mais próximos dos valores de referência para as linhas
        const findNearestIndex = useMemo(() => {
            return (value) => {
                if (chartData.length === 0) return 0;
                let nearestIndex = 0;
                let minDiff = Math.abs(chartData[0].valores - value);
                chartData.forEach((item, index) => {
                    const diff = Math.abs(item.valores - value);
                    if (diff < minDiff) {
                        minDiff = diff;
                        nearestIndex = index;
                    }
                });
                return nearestIndex;
            };
        }, [chartData]);

        if (!values || !Array.isArray(values) || values.length === 0 || chartData.length === 0) {
            return null;
        }

        return (
            <div className="bg-white rounded-lg p-6 mb-6" style={{ outline: 'none', border: 'none' }}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Distribuição Empírica da Transição de {transitionKey}
                </h3>
                <div style={{ position: 'relative', outline: 'none', border: 'none' }} tabIndex="-1" onMouseDown={(e) => e.preventDefault()}>
                    {/* Legenda customizada */}
                    <div style={{
                        position: 'absolute',
                        top: '30px',
                        right: '40px',
                        padding: '10px',
                        fontSize: '12px',
                        lineHeight: '1.6',
                        zIndex: 10
                    }}>
                        <div style={{ color: '#eab308' }}>--- Média = {formatNumber(media)}</div>
                        <div style={{ color: '#10b981' }}>--- P10 = {formatNumber(p10)}</div>
                        <div style={{ color: '#000' }}>--- P50 = {formatNumber(p50)}</div>
                        <div style={{ color: '#ea580c' }}>--- P75 = {formatNumber(p75)}</div>
                        <div style={{ color: '#dc2626' }}>--- P90 = {formatNumber(p90)}</div>
                    </div>
                    <ResponsiveContainer width="100%" height={500}>
                        <BarChart data={chartData} margin={{ top: 20, right: 20, left: 80, bottom: 50 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="valores"
                                label={{ value: 'Valores (dias)', position: 'insideBottom', offset: -10 }}
                            />
                            <YAxis label={{ value: 'Frequência', angle: -90, position: 'center', dx: -50 }} />
                            <Tooltip />
                            <Bar dataKey="frequencia" fill="#4169e1" isAnimationActive={false} />
                            {/* Linhas de referência */}
                            <ReferenceLine x={chartData[findNearestIndex(media)]?.valores} stroke="#eab308" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(p10)]?.valores} stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(p50)]?.valores} stroke="#000" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(p75)]?.valores} stroke="#ea580c" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(p90)]?.valores} stroke="#dc2626" strokeWidth={2} strokeDasharray="5 5" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    // Gráfico de Random Forest (histograma azul com linhas de referência)
    const RandomForestChart = ({ transitionKey, rfData }) => {
        const chartData = useMemo(() => {
            if (!rfData || !rfData.histograma || !Array.isArray(rfData.histograma) || rfData.histograma.length === 0) {
                return [];
            }

            const values = rfData.histograma;
            const minVal = Math.min(...values);
            const maxVal = Math.max(...values);
            const numBins = 20;
            const binWidth = (maxVal - minVal) / numBins;

            const bins = Array(numBins).fill(0).map((_, i) => ({
                start: minVal + i * binWidth,
                end: minVal + (i + 1) * binWidth,
                count: 0
            }));

            values.forEach(val => {
                const binIndex = Math.min(Math.floor((val - minVal) / binWidth), numBins - 1);
                if (binIndex >= 0 && binIndex < numBins) {
                    bins[binIndex].count++;
                }
            });

            return bins.map((bin) => ({
                tempo: Math.round((bin.start + bin.end) / 2),
                frequencia: bin.count
            }));
        }, [rfData]);

        // Encontrar índices mais próximos dos valores de referência para as linhas
        const findNearestIndex = useMemo(() => {
            return (value) => {
                if (chartData.length === 0) return 0;
                let nearestIndex = 0;
                let minDiff = Math.abs(chartData[0].tempo - value);
                chartData.forEach((item, index) => {
                    const diff = Math.abs(item.tempo - value);
                    if (diff < minDiff) {
                        minDiff = diff;
                        nearestIndex = index;
                    }
                });
                return nearestIndex;
            };
        }, [chartData]);

        if (!rfData || !rfData.histograma || chartData.length === 0) {
            return null;
        }

        return (
            <div className="bg-white rounded-lg p-6 mb-6" style={{ outline: 'none', border: 'none' }}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Random Forest da Transição de {transitionKey}
                </h3>
                <div style={{ position: 'relative', outline: 'none', border: 'none' }} tabIndex="-1" onMouseDown={(e) => e.preventDefault()}>
                    {/* Legenda customizada */}
                    <div style={{
                        position: 'absolute',
                        top: '30px',
                        right: '40px',
                        padding: '10px',
                        fontSize: '12px',
                        lineHeight: '1.6',
                        zIndex: 10
                    }}>
                        <div style={{ color: '#eab308' }}>--- Média = {formatNumber(rfData.media)}</div>
                        <div style={{ color: '#10b981' }}>--- P10 = {formatNumber(rfData.p10)}</div>
                        <div style={{ color: '#000' }}>--- P50 = {formatNumber(rfData.p50)}</div>
                        <div style={{ color: '#ea580c' }}>--- P75 = {formatNumber(rfData.p75)}</div>
                        <div style={{ color: '#dc2626' }}>--- P90 = {formatNumber(rfData.p90)}</div>
                    </div>
                    <ResponsiveContainer width="100%" height={500}>
                        <BarChart data={chartData} margin={{ top: 20, right: 20, left: 80, bottom: 50 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="tempo"
                                label={{ value: 'Valor previsto (dias)', position: 'insideBottom', offset: -10 }}
                            />
                            <YAxis label={{ value: 'Frequência', angle: -90, position: 'center', dx: -50 }} />
                            <Tooltip />
                            <Bar dataKey="frequencia" fill="#4169e1" isAnimationActive={false} />
                            {/* Linhas de referência */}
                            <ReferenceLine x={chartData[findNearestIndex(rfData.media)]?.tempo} stroke="#eab308" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(rfData.p10)]?.tempo} stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(rfData.p50)]?.tempo} stroke="#000" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(rfData.p75)]?.tempo} stroke="#ea580c" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(rfData.p90)]?.tempo} stroke="#dc2626" strokeWidth={2} strokeDasharray="5 5" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    // Tabela de Distribuições Ajustadas
    const FittedDistributionsTable = ({ distData, title, type }) => {
        if (!distData) {
            return null;
        }

        const allTransitions = Object.keys(distData).sort();
        const transitions = type
            ? allTransitions.filter(key => key.toUpperCase().includes(type.toUpperCase()))
            : allTransitions;

        if (transitions.length === 0) {
            return null;
        }

        return (
            <div className="overflow-x-auto mb-6">
                {title && <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>}
                <table className="min-w-full divide-y divide-gray-200" style={{ outline: 'none' }}>
                    <thead className="bg-gray-50">
                        <tr style={{ outline: 'none' }}>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transição</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distribuição</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parâmetros</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">LogLik</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AIC</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">BIC</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">KS Stat</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">KS p-valor</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shapiro Stat</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shapiro p-valor</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {transitions.map((key, index) => {
                            const dist = distData[key].distMelhor;
                            return (
                                <tr key={index} style={{ outline: 'none' }}>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {key}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {dist.distribuicao}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <ParameterTooltip distribution={dist.distribuicao} parameters={dist.params}>
                                                {/* <HiInformationCircle
                                                    style={{
                                                        cursor: 'help',
                                                        color: '#3b82f6',
                                                        fontSize: '14px'
                                                    }}
                                                /> */}
                                                [{dist.params.map(p => formatNumber(p)).join(', ')}]

                                            </ParameterTooltip>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(dist.loglik)}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(dist.aic)}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(dist.bic)}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(dist.ks_stat)}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(dist.ks_p)}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(dist.shapiro_stat)}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(dist.shapiro_p)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    // Tabela de Distribuições Empíricas
    const EmpiricalDistributionsTable = ({ distData, title, type }) => {
        if (!distData || !distData.dados) {
            return null;
        }

        const allTransitions = Object.keys(distData.dados).sort();
        const transitions = type
            ? allTransitions.filter(key => key.toUpperCase().includes(type.toUpperCase()))
            : allTransitions;

        if (transitions.length === 0) {
            return null;
        }

        return (
            <div className="overflow-x-auto mb-6">
                {title && <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>}
                <table className="min-w-full divide-y divide-gray-200" style={{ outline: 'none' }}>
                    <thead className="bg-gray-50">
                        <tr style={{ outline: 'none' }}>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transição</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Média (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P10 (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P50 (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P75 (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P90 (dia)</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {transitions.map((key, index) => {
                            return (
                                <tr key={index} style={{ outline: 'none' }}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {key}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {distData.media && distData.media[key] ? formatNumber(distData.media[key]) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {distData.p10 && distData.p10[key] ? formatNumber(distData.p10[key]) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {distData.p50 && distData.p50[key] ? formatNumber(distData.p50[key]) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {distData.p75 && distData.p75[key] ? formatNumber(distData.p75[key]) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {distData.p90 && distData.p90[key] ? formatNumber(distData.p90[key]) : '-'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    // Tabela de Random Forest
    const RandomForestTable = ({ rfData, title, type }) => {
        if (!rfData) {
            return null;
        }

        const allTransitions = Object.keys(rfData).sort();
        const transitions = type
            ? allTransitions.filter(key => key.toUpperCase().includes(type.toUpperCase()))
            : allTransitions;

        if (transitions.length === 0) {
            return null;
        }

        return (
            <div className="overflow-x-auto mb-6">
                {title && <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>}
                <table className="min-w-full divide-y divide-gray-200" style={{ outline: 'none' }}>
                    <thead className="bg-gray-50">
                        <tr style={{ outline: 'none' }}>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transição</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Média (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Desvio (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P10 (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P50 (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P75 (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P90 (dia)</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {transitions.map((key, index) => {
                            const rf = rfData[key];
                            return (
                                <tr key={index} style={{ outline: 'none' }}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {key}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(rf.media)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(rf.desvio)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(rf.p10)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(rf.p50)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(rf.p75)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(rf.p90)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    // Renderizar conteúdo baseado no método selecionado
    const renderContent = () => {
        if (selectedMethod === 'AJUSTADAS (GRÁFICO)') {
            if (!distributionData.distAjustadas) {
                return (
                    <div className="text-center text-gray-500 py-12">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum dado de distribuições ajustadas disponível</p>
                    </div>
                );
            }

            const allTransitions = Object.keys(distributionData.distAjustadas).sort();
            const trlTransitions = allTransitions.filter(key => key.toUpperCase().includes('TRL'));
            const crlTransitions = allTransitions.filter(key => key.toUpperCase().includes('CRL'));

            return (
                <div>
                    {/* TRL Section */}
                    {trlTransitions.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                                Distribuições Ajustadas - TRL
                            </h2>
                            {trlTransitions.map(key => (
                                <FittedDistributionChart
                                    key={key}
                                    transitionKey={key}
                                    distData={distributionData.distAjustadas[key]}
                                    empiricalValues={distributionData.distEmpiricas?.dados?.[key]}
                                />
                            ))}
                        </div>
                    )}

                    {/* CRL Section */}
                    {crlTransitions.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-green-500 pb-2">
                                Distribuições Ajustadas - CRL
                            </h2>
                            {crlTransitions.map(key => (
                                <FittedDistributionChart
                                    key={key}
                                    transitionKey={key}
                                    distData={distributionData.distAjustadas[key]}
                                    empiricalValues={distributionData.distEmpiricas?.dados?.[key]}
                                />
                            ))}
                        </div>
                    )}
                </div>
            );
        } else if (selectedMethod === 'EMPÍRICAS (GRÁFICO)') {
            if (!distributionData.distEmpiricas || !distributionData.distEmpiricas.dados) {
                return (
                    <div className="text-center text-gray-500 py-12">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum dado de distribuições empíricas disponível</p>
                    </div>
                );
            }

            const allTransitions = Object.keys(distributionData.distEmpiricas.dados).sort();
            const trlTransitions = allTransitions.filter(key => key.toUpperCase().includes('TRL'));
            const crlTransitions = allTransitions.filter(key => key.toUpperCase().includes('CRL'));

            return (
                <div>
                    {/* TRL Section */}
                    {trlTransitions.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                                Distribuições Empíricas - TRL
                            </h2>
                            {trlTransitions.map(key => (
                                <EmpiricalDistributionChart
                                    key={key}
                                    transitionKey={key}
                                    values={distributionData.distEmpiricas.dados[key]}
                                    media={distributionData.distEmpiricas.media?.[key]}
                                    p10={distributionData.distEmpiricas.p10?.[key]}
                                    p50={distributionData.distEmpiricas.p50?.[key]}
                                    p75={distributionData.distEmpiricas.p75?.[key]}
                                    p90={distributionData.distEmpiricas.p90?.[key]}
                                />
                            ))}
                        </div>
                    )}

                    {/* CRL Section */}
                    {crlTransitions.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-green-500 pb-2">
                                Distribuições Empíricas - CRL
                            </h2>
                            {crlTransitions.map(key => (
                                <EmpiricalDistributionChart
                                    key={key}
                                    transitionKey={key}
                                    values={distributionData.distEmpiricas.dados[key]}
                                    media={distributionData.distEmpiricas.media?.[key]}
                                    p10={distributionData.distEmpiricas.p10?.[key]}
                                    p50={distributionData.distEmpiricas.p50?.[key]}
                                    p75={distributionData.distEmpiricas.p75?.[key]}
                                    p90={distributionData.distEmpiricas.p90?.[key]}
                                />
                            ))}
                        </div>
                    )}
                </div>
            );
        } else if (selectedMethod === 'RANDOM FOREST (GRÁFICO)') {
            if (!distributionData.distRndForest) {
                return (
                    <div className="text-center text-gray-500 py-12">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum dado de Random Forest disponível</p>
                    </div>
                );
            }

            const allTransitions = Object.keys(distributionData.distRndForest).sort();
            const trlTransitions = allTransitions.filter(key => key.toUpperCase().includes('TRL'));
            const crlTransitions = allTransitions.filter(key => key.toUpperCase().includes('CRL'));

            return (
                <div>
                    {/* TRL Section */}
                    {trlTransitions.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                                Random Forest - TRL
                            </h2>
                            {trlTransitions.map(key => (
                                <RandomForestChart
                                    key={key}
                                    transitionKey={key}
                                    rfData={distributionData.distRndForest[key]}
                                />
                            ))}
                        </div>
                    )}

                    {/* CRL Section */}
                    {crlTransitions.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-green-500 pb-2">
                                Random Forest - CRL
                            </h2>
                            {crlTransitions.map(key => (
                                <RandomForestChart
                                    key={key}
                                    transitionKey={key}
                                    rfData={distributionData.distRndForest[key]}
                                />
                            ))}
                        </div>
                    )}
                </div>
            );
        } else if (selectedMethod === 'AJUSTADAS (TABELA)') {
            if (!distributionData.distAjustadas) {
                return (
                    <div className="text-center text-gray-500 py-12">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum dado de distribuições ajustadas disponível</p>
                    </div>
                );
            }

            return (
                <div className="bg-white rounded-lg p-6">
                    <FittedDistributionsTable
                        distData={distributionData.distAjustadas}
                        title="Distribuições Ajustadas - TRL"
                        type="TRL"
                    />
                    <FittedDistributionsTable
                        distData={distributionData.distAjustadas}
                        title="Distribuições Ajustadas - CRL"
                        type="CRL"
                    />
                </div>
            );
        } else if (selectedMethod === 'EMPÍRICAS (TABELA)') {
            if (!distributionData.distEmpiricas || !distributionData.distEmpiricas.dados) {
                return (
                    <div className="text-center text-gray-500 py-12">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum dado de distribuições empíricas disponível</p>
                    </div>
                );
            }

            return (
                <div className="bg-white rounded-lg p-6">
                    <EmpiricalDistributionsTable
                        distData={distributionData.distEmpiricas}
                        title="Distribuições Empíricas - TRL"
                        type="TRL"
                    />
                    <EmpiricalDistributionsTable
                        distData={distributionData.distEmpiricas}
                        title="Distribuições Empíricas - CRL"
                        type="CRL"
                    />
                </div>
            );
        } else if (selectedMethod === 'RANDOM FOREST (TABELA)') {
            if (!distributionData.distRndForest) {
                return (
                    <div className="text-center text-gray-500 py-12">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum dado de Random Forest disponível</p>
                    </div>
                );
            }

            return (
                <div className="bg-white rounded-lg p-6">
                    <RandomForestTable
                        rfData={distributionData.distRndForest}
                        title="Random Forest - TRL"
                        type="TRL"
                    />
                    <RandomForestTable
                        rfData={distributionData.distRndForest}
                        title="Random Forest - CRL"
                        type="CRL"
                    />
                </div>
            );
        }
    };

    return (
        <div className="p-6 h-full overflow-auto custom-scrollbar">
            {/* Select dropdown */}
            <div className="mb-6">
                <div className="relative inline-block">
                    <select
                        value={selectedMethod}
                        onChange={(e) => setSelectedMethod(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 rounded px-4 py-2 pr-8 text-sm text-gray-700 focus:border-gray-400 min-w-40"
                    >
                        <option value="EMPÍRICAS (TABELA)">EMPÍRICAS (TABELA)</option>
                        <option value="EMPÍRICAS (GRÁFICO)">EMPÍRICAS (GRÁFICO)</option>
                        <option value="AJUSTADAS (TABELA)">AJUSTADAS (TABELA)</option>
                        <option value="AJUSTADAS (GRÁFICO)">AJUSTADAS (GRÁFICO)</option>
                        <option value="RANDOM FOREST (TABELA)">RANDOM FOREST (TABELA)</option>
                        <option value="RANDOM FOREST (GRÁFICO)">RANDOM FOREST (GRÁFICO)</option>
                    </select>
                    <HiChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
            </div>

            {/* Content */}
            {renderContent()}
        </div>
    );
};

export default DistributionsTab;
